import React from 'react';

import { ArtifactAudit, BlobSinkAction } from '@/services/types/blob-types';

import {
  getIndentStyle,
  treeStyles,
  TreeViewSize,
} from '../constants/treeStyles';
import { AuditIndicator } from './AuditIndicator';
import { SelectionIndicator } from './SelectionIndicator';
import { SinkActionIndicator } from './SinkActionIndicator';

interface BaseNodeItemProps {
  level: number;
  className?: string;
  isSelected: boolean;
  icon?: React.ReactNode;
  label: string | React.ReactNode;
  actions?: React.ReactNode;
  stats?: React.ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick: (e: React.MouseEvent) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  'aria-label'?: string;
  'aria-expanded'?: boolean;
  children?: React.ReactNode;
  selectable?: boolean;
  selectionChecked?: boolean;
  onSelectionChange?: (checked: boolean) => void;
  dataCollectionId?: string;
  dataBlobParentId?: string;
  size?: TreeViewSize;
  /** Sink action to display (if not PUSHED) */
  sinkAction?: BlobSinkAction;
  /** Matter ID for sink action API calls */
  matterId?: string;
  /** Artifact audit data to display */
  auditData?: ArtifactAudit;
  /** Artifact ID for audit API calls */
  artifactId?: string;
}

export const BaseNodeItem = ({
  level,
  className = '',
  isSelected,
  icon,
  label,
  actions,
  stats,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onKeyDown,
  'aria-label': ariaLabel,
  'aria-expanded': ariaExpanded,
  children,
  selectable = false,
  selectionChecked = false,
  onSelectionChange,
  dataCollectionId,
  dataBlobParentId,
  size = 'sm',
  sinkAction,
  matterId,
  auditData,
  artifactId,
}: BaseNodeItemProps) => {
  // Get size-specific styles
  const sizeStyles = treeStyles.row[size];
  const actionsSpacing = treeStyles.spacing.actions[size];

  // Render children or content based on props
  const renderContent = () => {
    if (children) {
      return children;
    }

    return (
      <>
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <div className="flex items-center justify-between w-full min-w-0">
          <span className="flex items-center gap-1.5 truncate">
            <span className="truncate font-medium select-none">{label}</span>
          </span>
          <div className="flex items-center gap-1">
            {stats && (
              <div className="text-muted-foreground select-none">{stats}</div>
            )}
            {auditData && artifactId && matterId && (
              <AuditIndicator
                audit={auditData}
                artifactId={artifactId}
                matterId={matterId}
              />
            )}
            {sinkAction && matterId && (
              <SinkActionIndicator
                sinkAction={sinkAction}
                matterId={matterId}
              />
            )}
            {actions && (
              <div
                className={`flex items-center flex-shrink-0 ${actionsSpacing}`}
              >
                {actions}
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div
      className={`@container group relative flex items-center ${sizeStyles.class} ${sizeStyles.padding} ${sizeStyles.fontSize}  rounded-sm cursor-pointer ${
        isSelected ? 'bg-gray-200' : 'hover:bg-gray-100'
      } ${className}`}
      style={getIndentStyle(level, size)}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      data-collection-id={dataCollectionId}
      data-blob-parent-id={dataBlobParentId}
    >
      {selectable && (
        <SelectionIndicator
          checked={selectionChecked}
          onChange={onSelectionChange || (() => {})}
          className={`absolute ${treeStyles.selection.indicator[size]} selection-indicator`}
        />
      )}
      {renderContent()}
    </div>
  );
};
